class StayAISubscriptions extends HTMLElement {
  constructor() {
    super();
    this.productJSON = this.getAttribute("productData");
    this.product = JSON.parse(this.productJSON);
    this.initialVariant = this.getAttribute("currentVariantId");
    this.sellingPlanGroups = JSON.parse(this.getAttribute("sellingPlanGroups"));
    this.productFormId = this.getAttribute("productFormId");
    this.frequencyVisible = this.getAttribute("frequencyVisible");
    // Step 1/3 — prepaid SPG ids (CSV) emitted by Liquid; empty when product has no prepaid plans
    const prepaidIdsCsv = this.getAttribute("prepaidGroupIds") || "";
    this.prepaidGroupIds = new Set(
      prepaidIdsCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(String),
    );
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }
  initWidget() {
    // Step 3 — 3-way state: standard subscription label keeps id `purchaseTypeSubscription`;
    // the new prepaid label has id `purchaseTypeSubscriptionPrepaid`. Both may be absent
    // (one-time only product) or the prepaid one may be absent (no prepaid SPGs).
    this.purchaseTypeSubscription = this.querySelector("[for='purchaseTypeSubscription']");
    this.purchaseTypeSubscriptionPrepaid = this.querySelector("[for='purchaseTypeSubscriptionPrepaid']");
    this.purchaseTypeOneTime = this.querySelector("[for='purchaseTypeOneTime']");
    // Subscribe-and-save dropdown + wrapper (not prepaid).
    // bubble-buttons, split-button, and side-by-side only have one dropdown, so the || fallbacks
    // target that element when prepaid markup isn't in the DOM.
    // Classes standard-subscription-type / prepaid-subscription-type avoid clashing with
    // widget_layout values like 'standard' and 'side-by-side-buttons'.
    this.sellingPlanSelector =
      this.querySelector(".stayai-subscription-dropdown.standard-subscription-type") ||
      this.querySelector(".stayai-subscription-dropdown");
    this.sellingPlanSelectorPrepaid = this.querySelector(".stayai-subscription-dropdown.prepaid-subscription-type");
    this.subscriptionBox =
      this.querySelector("[data-stayai-subscription-type='standard']") ||
      this.querySelector("[data-retextion-subscription-box]");
    this.subscriptionBoxPrepaid = this.querySelector("[data-stayai-subscription-type='prepaid']");
    this.subscriptionBoxContainer = this.querySelector(".stayai-subscription-frequency-container");
    this.radioOneTime = this.querySelector('input[value="purchaseTypeOneTime"]');
    this.radioSubscription = this.querySelector('input[value="purchaseTypeSubscription"]');
    this.radioSubscriptionPrepaid = this.querySelector('input[value="purchaseTypeSubscriptionPrepaid"]');
    const selectedInitialVariant = this.product.variants.find((variant) => variant.id === Number(this.initialVariant));
    this.currentVariant = selectedInitialVariant || this.product.variants[0];
    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    const purchaseTypeRadioButtons = this.querySelectorAll('input[name="purchaseType"]');
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach((radio) =>
      radio.addEventListener("change", () => this.onPurchaseTypeChange(radio.value)),
    );
    // Handle standard frequency selector change
    if (this.sellingPlanSelector) {
      this.sellingPlanSelector.addEventListener("change", (e) => {
        this.updateSellingPlanInput(e.target.value);
        this.updateProductPrice(e.target.value);
      });
    }
    // Handle prepaid frequency selector change
    if (this.sellingPlanSelectorPrepaid) {
      this.sellingPlanSelectorPrepaid.addEventListener("change", (e) => {
        this.updateSellingPlanInput(e.target.value);
        this.updateProductPrice(e.target.value);
      });
    }
    // Handle variant change from URL
    window.addEventListener("popstate", this.updateVariantID());
    this.observeUrlChanges();
    // Handle browser back/forward navigation.
    // Why this exists: on back-nav from checkout, Liquid re-renders with the server default
    // (e.g. subscription label gets `is-selected`), but the browser's form-state restoration
    // flips `input.checked` back to the user's last choice AFTER our JS init runs. Result:
    // label's `is-selected` class and the underlying radio's `checked` state diverge.
    //
    // Fix: re-sync from radio.checked on every pageshow. We can't use `label.click()` —
    // clicking a label whose radio is already `checked` does not fire a `change` event.
    // Also do not gate on `event.persisted` / navigation.type — Safari does form-state
    // restoration without bfcache, so those flags miss real back-nav cases.
    window.addEventListener("pageshow", () => this.syncStateFromCheckedRadio());
  }

  // Force is-selected classes + frequency-box visibility + hidden selling_plan input
  // to match whichever radio currently has `checked`. Idempotent on initial load.
  syncStateFromCheckedRadio() {
    const checkedRadio = this.querySelector('input[name="purchaseType"]:checked');
    if (checkedRadio) {
      this.onPurchaseTypeChange(checkedRadio.value);
    } else if (this.radioOneTime) {
      // Defensive fallback: no radio checked at all → reset to one-time
      this.radioOneTime.checked = true;
      this.onPurchaseTypeChange("purchaseTypeOneTime");
    }
    this.updateSellingPlans();
  }
  // Step 3 — central handler for radio change. Drives show/hide for all three states.
  onPurchaseTypeChange(value) {
    if (value === "purchaseTypeSubscription") {
      this.showSubscriptionBox("standard");
      this.updateProductPrice(this.sellingPlanSelector ? this.sellingPlanSelector.value : "");
    } else if (value === "purchaseTypeSubscriptionPrepaid") {
      this.showSubscriptionBox("prepaid");
      this.updateProductPrice(this.sellingPlanSelectorPrepaid ? this.sellingPlanSelectorPrepaid.value : "");
    } else {
      this.hideSubscriptionBox();
      this.updateProductPrice("");
    }
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("variant")) return;
    const variantId = Number(urlParams.get("variant"));
    if (variantId && variantId !== this.currentVariant.id) {
      this.currentVariant = this.product.variants.find((v) => v.id === variantId);
      this.updateSellingPlans();
    }
  };
  // Monitor URL changes without overriding history methods
  observeUrlChanges = () => {
    let lastUrl = window.location.href;
    new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.updateVariantID();
      }
    }).observe(document, { subtree: true, childList: true });
  };
  // Step 3 — return true if the given group id was flagged as prepaid by Liquid.
  isPrepaidGroup(groupId) {
    return this.prepaidGroupIds.has(String(groupId));
  }
  // Populate the subscription frequency selector(s) with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) return;
    // Only 'bundle' is filtered out entirely. 'gift'/'prepaid'/'prepay' are partition signals,
    // not exclusion criteria — Liquid put those SPG ids into prepaidGroupIds for us.
    const groupMap = new Map(
      this.sellingPlanGroups
        .filter((g) => !(g.options?.[0]?.name || "").toLowerCase().includes("bundle"))
        .map((g) => [String(g.id), g]),
    );
    // Partition allocations into standard vs prepaid based on prepaidGroupIds set
    const standardPlans = [];
    const prepaidPlans = [];
    this.currentVariant.selling_plan_allocations.forEach((spa) => {
      const group = groupMap.get(String(spa.selling_plan_group_id));
      const plan = group?.selling_plans.find((sp) => sp.id === spa.selling_plan_id);
      if (!plan) return;
      if (this.isPrepaidGroup(spa.selling_plan_group_id)) {
        prepaidPlans.push(plan);
      } else {
        standardPlans.push(plan);
      }
    });
    const totalPlans = standardPlans.length + prepaidPlans.length;
    if (totalPlans === 0) {
      this.updateSellingPlanInput("");
      stayWidget.classList.toggle("is-visible", false);
      console.log("No SPs, hiding widget");
      return;
    }
    stayWidget.classList.toggle("is-visible", true);
    // Populate the standard dropdown (this may be the only dropdown in non-`standard` widget layouts)
    if (this.sellingPlanSelector) {
      this.sellingPlanSelector.innerHTML = standardPlans
        .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
        .join("");
    }
    // Populate the prepaid dropdown when present
    if (this.sellingPlanSelectorPrepaid) {
      this.sellingPlanSelectorPrepaid.innerHTML = prepaidPlans
        .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
        .join("");
    }
    // Determine which subscription type (if any) is currently selected
    const selectedType = this.getSelectedSubscriptionType();
    if (selectedType === "standard") {
      this.removeSelectFrequencyOption(this.sellingPlanSelector);
      this.updateSellingPlanInput(this.sellingPlanSelector?.value || "");
    } else if (selectedType === "prepaid") {
      this.removeSelectFrequencyOption(this.sellingPlanSelectorPrepaid);
      this.updateSellingPlanInput(this.sellingPlanSelectorPrepaid?.value || "");
    } else {
      this.addSelectFrequencyOption(this.sellingPlanSelector);
      this.addSelectFrequencyOption(this.sellingPlanSelectorPrepaid);
      this.updateSellingPlanInput("");
    }
    // Show or hide the subscription frequency selector(s) based on the current selection
    this.subscriptionBox?.classList.toggle("is-visible", selectedType === "standard");
    this.subscriptionBoxPrepaid?.classList.toggle("is-visible", selectedType === "prepaid");
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", selectedType !== null);
    // Pre-paint the prepaid price label even when prepaid radio isn't active, so the
    // strikethrough ($variant.price × shipmentCount) shows on initial paint. updateProductPrice
    // routes writes by allocation.group → this only touches prepaid labels, doesn't clobber
    // the standard/one-time labels updated by the active-plan call below.
    if (this.sellingPlanSelectorPrepaid?.value) {
      this.updateProductPrice(this.sellingPlanSelectorPrepaid.value);
    }
    // Update prices for the currently-active plan
    if (selectedType === "standard") {
      this.updateProductPrice(this.sellingPlanSelector?.value || standardPlans[0]?.id || "");
    } else if (selectedType === "prepaid") {
      this.updateProductPrice(this.sellingPlanSelectorPrepaid?.value || prepaidPlans[0]?.id || "");
    } else {
      this.updateProductPrice("");
    }
  }
  // Returns 'standard' | 'prepaid' | null based on which radio currently has is-selected on its label
  getSelectedSubscriptionType() {
    if (this.purchaseTypeSubscriptionPrepaid?.classList.contains("is-selected")) return "prepaid";
    if (this.purchaseTypeSubscription?.classList.contains("is-selected")) return "standard";
    return null;
  }
  // Add "Select frequency" option to a frequency selector
  addSelectFrequencyOption(selector) {
    if (!selector) return;
    if (!selector.querySelector('option[value=""]')) {
      selector.insertAdjacentHTML("afterbegin", '<option value="" selected>Select frequency</option>');
    }
  }
  removeSelectFrequencyOption(selector) {
    if (!selector) return;
    selector.querySelector('option[value=""]')?.remove();
  }
  // Show the frequency selector for the given subscription type ('standard' | 'prepaid')
  showSubscriptionBox(subscriptionType = "standard") {
    const isPrepaid = subscriptionType === "prepaid";
    const activeBox = isPrepaid ? this.subscriptionBoxPrepaid : this.subscriptionBox;
    const inactiveBox = isPrepaid ? this.subscriptionBox : this.subscriptionBoxPrepaid;
    const activeSelector = isPrepaid ? this.sellingPlanSelectorPrepaid : this.sellingPlanSelector;
    activeBox?.classList.toggle("stayai-disable", false);
    activeBox?.classList.toggle("is-visible", true);
    inactiveBox?.classList.toggle("is-visible", false);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", true);
    this.purchaseTypeOneTime?.classList.toggle("is-selected", false);
    if (isPrepaid) {
      this.purchaseTypeSubscription?.classList.toggle("is-selected", false);
      this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", true);
    } else {
      this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", false);
      this.purchaseTypeSubscription?.classList.toggle("is-selected", true);
    }
    this.removeSelectFrequencyOption(activeSelector);
    this.updateSellingPlanInput(activeSelector?.value || "");
  }
  // Hide both frequency selectors (one-time selected)
  hideSubscriptionBox() {
    this.addSelectFrequencyOption(this.sellingPlanSelector);
    this.addSelectFrequencyOption(this.sellingPlanSelectorPrepaid);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("is-visible", false);
    this.subscriptionBox?.classList.toggle("is-visible", false);
    this.subscriptionBox?.classList.toggle("disable", true);
    this.subscriptionBoxPrepaid?.classList.toggle("is-visible", false);
    this.subscriptionBoxPrepaid?.classList.toggle("disable", true);
    this.purchaseTypeSubscription?.classList.toggle("is-selected", false);
    this.purchaseTypeSubscriptionPrepaid?.classList.toggle("is-selected", false);
    this.purchaseTypeOneTime?.classList.toggle("is-selected", true);
    this.updateSellingPlanInput("");
  }
  // Check if any subscription option (standard or prepaid) is selected
  isSubscriptionOptionSelected() {
    return this.getSelectedSubscriptionType() !== null;
  }
  //Add selling plan input to form with specified selling plan ID
  updateSellingPlanInput(sellingPlanId) {
    let productForm = document.querySelector(
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
        'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
        'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
        'form[action="/cart/add"]:not(.installment)',
    );
    if (this.productFormId !== "") productForm = document.querySelector(this.productFormId) || productForm;
    if (!productForm) return;

    // Find or create the hidden input for selling plan
    let sellingPlanInput = productForm.querySelector("input[name='selling_plan']");
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      sellingPlanInput.value = sellingPlanId;
      productForm.appendChild(sellingPlanInput);
    } else {
      sellingPlanInput.value = sellingPlanId;
    }
    // Dispatch event with the selling plan value
    this.fireStayAiEvent("stay-ai-subscription-changed", sellingPlanId);
  }
  updateProductPrice(sellingPlanId) {
    const selectedSellingPlanAllocation = this.currentVariant.selling_plan_allocations.find(
      (spa) => spa.selling_plan_id == sellingPlanId,
    );
    const sellingPlan = this.sellingPlanGroups
      .find((group) => group.selling_plans.some((sp) => sp.id == sellingPlanId))
      ?.selling_plans.find((sp) => sp.id == sellingPlanId);
    const oneTimeLblPrice = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPrice = this.querySelector("[data-stayai-subscription-price]");
    const prepaidLblPrice = this.querySelector("[data-stayai-prepaid-price]");
    const savingLbl = this.querySelector(".subscription-savings:not(.prepaid-savings)");
    const prepaidSavingLbl = this.querySelector(".prepaid-savings");
    const onetimePrice = this.formatMoney(this.currentVariant.price);
    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);
    let subscriptionPrice = onetimePrice;

    // Update one-time price
    if (oneTimeLblPrice && oneTimeLblPrice.textContent !== onetimePrice) {
      oneTimeLblPrice.innerHTML =
        compareAtPrice !== "$0.00" && compareAtPrice !== onetimePrice
          ? `<s>${compareAtPrice}</s> ${onetimePrice}`
          : onetimePrice;
    }

    // Update subscription/prepaid price for the active plan
    if (selectedSellingPlanAllocation) {
      subscriptionPrice = this.formatMoney(selectedSellingPlanAllocation.price);
      const isPrepaidActive = this.isPrepaidGroup(selectedSellingPlanAllocation.selling_plan_group_id);
      const targetPriceLbl = isPrepaidActive ? prepaidLblPrice : subscriptionLblPrice;
      const targetSavingLbl = isPrepaidActive ? prepaidSavingLbl : savingLbl;
      // Strikethrough price: for prepaid, allocation.price is the total across N
      // shipments, so the strikethrough must also span N shipments to be apples-to-apples.
      // Parse N from the plan label, e.g. "Prepaid Subscription - Ships every month, 3 total shipments".
      let strikethroughPrice = onetimePrice;
      if (isPrepaidActive) {
        const planLabel = sellingPlan?.options?.[0]?.value || sellingPlan?.name || "";
        const match = planLabel.match(/(\d+)\s*total\s*shipments?/i);
        const shipmentCount = match ? parseInt(match[1], 10) : 1;
        strikethroughPrice = this.formatMoney(this.currentVariant.price * shipmentCount);
      }
      // Compare against innerHTML, not textContent — on initial paint the Liquid-rendered
      // textContent may equal `subscriptionPrice` (e.g. prepaid label has "$38.22" already)
      // while the desired innerHTML still differs because we're adding a strikethrough.
      if (targetPriceLbl) {
        const desiredHtml =
          subscriptionPrice !== strikethroughPrice
            ? `<s class="fullPrice">${strikethroughPrice}</s>${subscriptionPrice}`
            : subscriptionPrice;
        if (targetPriceLbl.innerHTML !== desiredHtml) targetPriceLbl.innerHTML = desiredHtml;
      }
      if (isPrepaidActive) {
        // Prepaid path — allocation.price is the bundle total (e.g. 3 shipments × unit price),
        // so `variant.price - allocation.price` is negative and the standard gate below would
        // clear the savings label. Read the discount directly from the plan's price_adjustments
        // instead. `value` here is per-shipment percent off (or per-shipment money amount).
        const adj = sellingPlan?.price_adjustments?.[0];
        if (adj && targetSavingLbl && adj.value > 0) {
          const text = adj.value_type === "percentage" ? `${adj.value}%` : this.formatMoney(adj.value);
          if (targetSavingLbl.textContent !== text) targetSavingLbl.textContent = text;
        } else if (targetSavingLbl) {
          targetSavingLbl.textContent = "";
        }
      } else {
        // Standard subscribe-and-save path — discount = unit price diff.
        let discount = this.currentVariant.price - selectedSellingPlanAllocation.price;
        if (discount > 0 && targetSavingLbl) {
          if (sellingPlan?.price_adjustments[0].value_type === "percentage") {
            discount = `${sellingPlan.price_adjustments[0].value}%`;
          } else {
            discount = this.formatMoney(discount);
          }
          if (targetSavingLbl.textContent !== discount) targetSavingLbl.textContent = discount;
        } else if (targetSavingLbl) {
          targetSavingLbl.textContent = "";
        }
      }
    }
  }
  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || "USD";
    const locale = Shopify?.locale || "en";
    const hasShopifyFormatMoney = Shopify && typeof Shopify?.formatMoney === "function";
    const hasThemeUtilsFormatMoney = theme?.utils && typeof theme?.utils?.formatMoney === "function";
    const formattedMoney = (money / 100).toFixed(2);
    let formatted;
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    formatted = hasShopifyFormatMoney ? Shopify.formatMoney(money) : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId,
      },
    });
    window.dispatchEvent(event);
  }
}
// Define the custom element
customElements.define("purchase-options", StayAISubscriptions);